import React from 'react'
import { Link, useForm } from '@inertiajs/react'
import { CellMono, CellUser } from '@/admin/shared/components/DataTable/Cell'
import Badge from '@/admin/shared/components/Badge/Badge'
import type { DataTableColumn } from '@/admin/shared/components/DataTable/DataTable'
import type { AnalyticsSession } from '../types'
import tableStyles from '@/admin/shared/components/DataTable/DataTable.module.scss'

const cxTable = tableStyles

function formatDuration(started: string | null, lastSeen: string | null): string {
  if (!started || !lastSeen) return '—'
  const diff = new Date(lastSeen).getTime() - new Date(started).getTime()
  if (diff < 0) return '—'
  const minutes = Math.floor(diff / 60000)
  if (minutes < 1) return '<1m'
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  const remMin = minutes % 60
  if (hours < 24) return `${hours}h ${remMin}m`
  const days = Math.floor(hours / 24)
  return `${days}d ${hours % 24}h`
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export const COLUMNS: DataTableColumn<AnalyticsSession>[] = [
  {
    key: 'session_id',
    label: 'Session',
    render: s => <CellMono>{s.session_id.length > 20 ? s.session_id.slice(0, 20) + '…' : s.session_id}</CellMono>,
  },
  {
    key: 'device_type',
    label: 'Device',
    render: s => (
      <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={{ fontSize: '13px' }}>{s.device_type ?? '—'}</span>
        <span style={{ fontSize: '11px', color: 'var(--t-light)' }}>
          {s.browser ?? '—'} · {s.os ?? '—'}
        </span>
      </span>
    ),
  },
  {
    key: 'source',
    label: 'Source',
    render: s => <CellMono>{s.source ?? '—'}</CellMono>,
  },
  {
    key: 'users',
    label: 'User',
    render: s => {
      if (!s.users || s.users.length === 0) return <CellMono>—</CellMono>
      return (
        <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          {s.users.map(u => (
            <CellUser key={u.id} name={u.name} />
          ))}
        </span>
      )
    },
  },
  {
    key: 'ip',
    label: 'IP',
    render: s => <CellMono>{s.ip ?? '—'}</CellMono>,
  },
  {
    key: 'is_bot',
    label: 'Bot',
    render: s => (
      <Badge variant={s.is_bot ? 'warning' : 'muted'} dot>
        {s.is_bot ? 'Bot' : 'Human'}
      </Badge>
    ),
  },
  {
    key: 'events_count',
    label: 'Events',
    render: s => <CellMono>{s.events_count}</CellMono>,
  },
  {
    key: 'duration',
    label: 'Duration',
    render: s => <CellMono>{formatDuration(s.started_at, s.last_seen_at)}</CellMono>,
  },
  {
    key: 'started_at',
    label: 'Started',
    sortable: true,
    render: s => <CellMono>{formatDate(s.started_at)}</CellMono>,
  },
  {
    key: 'last_seen_at',
    label: 'Last Seen',
    sortable: true,
    render: s => <CellMono>{formatDate(s.last_seen_at)}</CellMono>,
  },
]

export function renderSessionActions(session: AnalyticsSession) {
  return <SessionActions session={session} />
}

function SessionActions({ session }: { session: AnalyticsSession }) {
  const { delete: destroy, processing } = useForm()

  function handleDelete() {
    if (!confirm('Delete this session and all its events?')) return
    destroy(`/admin/analytics/${session.id}`)
  }

  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      <Link href={`/admin/analytics/${session.id}`} className={cxTable.actionBtn}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
          <circle cx="12" cy="12" r="3" />
        </svg>
        Events
      </Link>
      <button
        type="button"
        className={cxTable.actionBtn}
        disabled={processing}
        onClick={handleDelete}
        title="Delete session"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M10 11v6M14 11v6" />
        </svg>
        {processing ? '…' : 'Delete'}
      </button>
    </div>
  )
}
